// Phone support: knowing we're on a touch screen, keeping the app above the on-screen keyboard, long-press, and
// edge-to-edge swipes for the channel drawer and threads.
import { useEffect, useRef, useState } from "react";

/** A finger, not a mouse: no hover, coarse pointer. Decides long-press menus vs hover toolbars, Enter behaviour… */
export const isTouch = () => typeof matchMedia !== "undefined" && matchMedia("(hover: none) and (pointer: coarse)").matches;

/** A text box the on-screen keyboard is for. */
export const isEditable = (el: Element | null) =>
  !!el && (el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && !["checkbox", "radio", "button", "submit", "range", "file", "color"].includes(el.type)) || (el as HTMLElement).isContentEditable);

/** Phone-sized layout (matches the CSS breakpoint). */
export const isNarrow = () => typeof matchMedia !== "undefined" && matchMedia("(max-width: 760px)").matches;

/**
 * Keeps the app exactly the size of the visible area. iOS Safari doesn't shrink the page when the keyboard opens
 * (it scrolls it instead), which would hide the message box; sizing the app to the visual viewport fixes that.
 */
export function installViewportSizing() {
  const vv = window.visualViewport;
  if (!vv) return;
  const root = document.documentElement;
  let frame = 0;
  // The tallest the visible area has been at this width: the screen without a keyboard. Android (resizes-content)
  // shrinks the whole window for the keyboard, so comparing with innerHeight alone would never see it there.
  let fullWidth = 0;
  let fullHeight = 0;
  const update = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      root.style.setProperty("--vvh", `${Math.round(vv.height)}px`);
      // The keyboard can still nudge the page; put it back so the header stays put.
      if (window.scrollY !== 0 && isNarrow()) window.scrollTo(0, 0);
      // iOS also slides the visible area down the (fixed) page to show the focused box: follow it, so the app is
      // exactly what's visible and the header stays on screen.
      root.style.setProperty("--vvt", `${Math.max(0, Math.round(vv.offsetTop))}px`);
      if (Math.round(vv.width) !== fullWidth) {
        // Rotated or resized: start over.
        fullWidth = Math.round(vv.width);
        fullHeight = 0;
      }
      fullHeight = Math.max(fullHeight, vv.height, window.innerHeight);
      const typing = isEditable(document.activeElement);
      root.classList.toggle("keyboard-open", isTouch() && typing && fullHeight - vv.height > 120);
    });
  };
  // Focus moving in or out of a text box is when the keyboard comes and goes; check again (the resize follows).
  document.addEventListener("focusin", update);
  document.addEventListener("focusout", () => setTimeout(update, 50));
  // Safari ignores user-scalable=no for pinches; its gesture events are the only way to keep the app unzoomed.
  const noZoom = (e: Event) => e.preventDefault();
  for (const type of ["gesturestart", "gesturechange", "gestureend"]) document.addEventListener(type, noZoom, { passive: false });
  vv.addEventListener("resize", update);
  vv.addEventListener("scroll", update);
  update();
}

/**
 * Touching a picker's results while typing in its search box puts the keyboard away (like Discord), so the results
 * get the whole sheet to scroll in instead of the sliver above the keyboard. Taps still pick: blurring doesn't
 * cancel the touch.
 */
export function dropKeyboard(e: React.TouchEvent) {
  const el = document.activeElement;
  if (el instanceof HTMLInputElement && el !== e.target && e.currentTarget.parentElement?.contains(el)) el.blur();
}

/** A light tap of haptic feedback where the device supports it (Android). */
export const buzz = () => {
  try {
    navigator.vibrate?.(8);
  } catch {
    // not supported
  }
};

/**
 * Long-press on touch screens: hold ~0.4 s without moving to fire. Returns handlers to spread on the element.
 * Moving (a scroll) or lifting early cancels it; the native callout menu is suppressed while it's armed.
 */
// One finger, one press at a time: kept outside the handlers so a re-render mid-press can't orphan the timer.
let pressTimer = 0;
let pressStart: { x: number; y: number } | null = null;
let pressFired = false;
const cancelPress = () => {
  clearTimeout(pressTimer);
  pressStart = null;
};

export function longPress(onFire: (target: HTMLElement) => void, ms = 420) {
  return {
    onTouchStart: (e: React.TouchEvent<HTMLElement>) => {
      cancelPress();
      pressFired = false;
      if (e.touches.length !== 1) return;
      const t = e.touches[0]!;
      pressStart = { x: t.clientX, y: t.clientY };
      const el = e.currentTarget;
      pressTimer = window.setTimeout(() => {
        pressStart = null;
        pressFired = true;
        buzz();
        onFire(el);
      }, ms);
    },
    onTouchMove: (e: React.TouchEvent<HTMLElement>) => {
      if (!pressStart) return;
      const t = e.touches[0]!;
      if (Math.hypot(t.clientX - pressStart.x, t.clientY - pressStart.y) > 8) cancelPress();
    },
    onTouchEnd: (e: React.TouchEvent) => {
      // The press already did its thing: don't let the lift also count as a tap on whatever opened under it.
      if (pressFired && e.cancelable) e.preventDefault();
      pressFired = false;
      cancelPress();
    },
    onTouchCancel: cancelPress,
    // Long-press on a link or image would otherwise also open the browser's own menu.
    onContextMenu: (e: React.MouseEvent) => {
      if (isTouch()) e.preventDefault();
    },
  };
}

interface SwipeOptions {
  /** Whether a swipe in that direction means anything right now. */
  canRight: () => boolean;
  canLeft: () => boolean;
  /** Called while dragging with the distance moved (px, signed) so the panel can follow the finger. */
  onDrag: (dx: number, dir: "right" | "left") => void;
  /** Finger lifted: commit (far or fast enough) or snap back. */
  onEnd: (commit: boolean, dir: "right" | "left") => void;
  /** How far the panel for that direction travels (px): a swipe commits past SWIPE.commitShare of it. */
  span?: (dir: "right" | "left") => number;
  /** Elements where horizontal drags belong to the content (a GIF strip, a text field…). */
  ignore?: string;
}

/**
 * How deliberate a swipe has to be. Swipes work anywhere on the chat (like Discord's), so they have to lose to
 * scrolling: a small or diagonal drag, a drag while the list is still coasting, or a short flick never opens a panel.
 */
export const SWIPE = {
  /** Movement before deciding between swipe and scroll (px). */
  slop: 16,
  /** A swipe must be this many times more horizontal than vertical (about 24° from flat). */
  ratio: 2.2,
  /** No swipe starts this soon after the content under the finger scrolled (ms): the list may still be coasting. */
  afterScroll: 300,
  /** Commit when dragged past this share of the panel's width… */
  commitShare: 0.4,
  /** …or flicked at least this fast (px/ms) over at least this far (px). */
  flickSpeed: 0.6,
  flickMin: 60,
} as const;

/**
 * A touch inside an app's full view, relayed by its frame (web/src/frame/gestures.ts) and dispatched by the bridge on
 * the iframe element as a bubbling CustomEvent of this name, in this page's coordinates.
 */
export const FRAME_TOUCH = "bloom-frame-touch";
export interface FrameTouch {
  phase: "start" | "move" | "end";
  x: number;
  y: number;
}

/**
 * Horizontal swipe with follow-the-finger feedback, on a whole area (like Discord's: swipe right anywhere on the
 * chat to reveal the channels). Scrolling wins unless the gesture is clearly horizontal from the start (SWIPE).
 */
export function useSwipe(ref: React.RefObject<HTMLElement | null>, opts: SwipeOptions) {
  const o = useRef(opts);
  o.current = opts;
  // The element can come later (the first renders show a loading screen) or be replaced: check after every render
  // and move the listeners to whatever the ref points at now. Before this, a cold start without the device cache
  // left the chat with no swipes at all.
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (ref.current !== el) setEl(ref.current);
  });
  useEffect(() => {
    if (!el) return;
    let sx = 0;
    let sy = 0;
    let st = 0;
    let mode: "undecided" | "swipe" | "scroll" | null = null;
    let dir: "right" | "left" = "right";
    let dx = 0;
    let lastScroll = 0;
    // Scroll events don't bubble; catch any scroller inside (the message list, a thread) on the way down.
    const scrolled = () => (lastScroll = performance.now());
    // A finger going down, moving and lifting: this page's own touches, or ones relayed out of an app's frame.
    const begin = (target: Element, x: number, y: number) => {
      if (!isNarrow()) return (mode = null);
      if (o.current.ignore && target.closest(o.current.ignore)) return (mode = null);
      // A touch that stops a coasting list is a scroll gesture, not the start of a swipe.
      if (performance.now() - lastScroll < SWIPE.afterScroll) return (mode = "scroll");
      sx = x;
      sy = y;
      st = performance.now();
      dx = 0;
      mode = "undecided";
    };
    /** Follows the finger; true while it's a swipe (so the page's own scrolling stays put). */
    const drag = (x: number, y: number) => {
      if (!mode || mode === "scroll") return false;
      const mx = x - sx;
      const my = y - sy;
      if (mode === "undecided") {
        if (Math.abs(mx) < SWIPE.slop && Math.abs(my) < SWIPE.slop) return false;
        const d = mx > 0 ? "right" : "left";
        if (Math.abs(mx) > Math.abs(my) * SWIPE.ratio && (d === "right" ? o.current.canRight() : o.current.canLeft())) {
          mode = "swipe";
          dir = d;
          // Follow from here, so the panel doesn't jump by the slop.
          sx = x;
          st = performance.now();
        } else {
          mode = "scroll";
          return false;
        }
      }
      const fx = x - sx;
      dx = dir === "right" ? Math.max(0, fx) : Math.min(0, fx);
      o.current.onDrag(dx, dir);
      return true;
    };
    const up = () => {
      if (mode === "swipe") {
        const dist = Math.abs(dx);
        const speed = dist / Math.max(1, performance.now() - st);
        const span = o.current.span?.(dir) ?? 300;
        o.current.onEnd(dist > span * SWIPE.commitShare || (speed > SWIPE.flickSpeed && dist > SWIPE.flickMin), dir);
      }
      mode = null;
    };
    const down = (e: TouchEvent) => {
      if (e.touches.length !== 1) return (mode = null);
      const t = e.touches[0]!;
      begin(e.target as Element, t.clientX, t.clientY);
    };
    const move = (e: TouchEvent) => {
      const t = e.touches[0]!;
      if (drag(t.clientX, t.clientY)) e.preventDefault();
    };
    // An app's full view is a cross-origin iframe, whose touches never reach this page. Its frame relays them through
    // the SDK bridge (apps/bridge.ts checks they come from that iframe), and they arrive here as events on the iframe.
    const relayed = (e: Event) => {
      const t = (e as CustomEvent<FrameTouch>).detail;
      if (t.phase === "start") begin(e.target as Element, t.x, t.y);
      else if (t.phase === "move") drag(t.x, t.y);
      else up();
    };
    el.addEventListener("scroll", scrolled, { capture: true, passive: true });
    el.addEventListener("touchstart", down, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", up);
    el.addEventListener("touchcancel", up);
    el.addEventListener(FRAME_TOUCH, relayed);
    return () => {
      el.removeEventListener("scroll", scrolled, { capture: true });
      el.removeEventListener("touchstart", down);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", up);
      el.removeEventListener("touchcancel", up);
      el.removeEventListener(FRAME_TOUCH, relayed);
    };
  }, [el]);
}
